import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { RootState } from "../../../store";
import { Spinner } from "../../../components/Spinner";
import { TablaTramitesComponent } from "../../../components/TablaTramitesComponent";
import {
  useGetPendientesQuery,
  useLazyGetPendientesQuery,
  useRmEspecialistaMutation,
} from "../../../services/docs";

export const MisPendientesScreen = () => {
  const navigate = useNavigate();
  const { usuario, roles } = useSelector((state: RootState) => state.ui);

  // Queries & Mutations
  const { data, isLoading } = useGetPendientesQuery();
  const [getPendientesAction, { isLoading: isLoadingL }] = useLazyGetPendientesQuery();
  const [rmEspecialistaAction, { isLoading: isLoadingE }] = useRmEspecialistaMutation();

  // Estados Locales
  const [temp, setTemp] = useState<any[]>([]);
  const [pendientes, setPendientes] = useState<any[]>([]);
  const [fc_agente, setFCAgente] = useState("");
  const [filtroActivo, setFiltroActivo] = useState({ etapaId: 0, subEtapaId: null as number | null });

  // Sincronización inicial
  useEffect(() => {
    if (data && data.pendientes) {
      setTemp(data.pendientes);
      setPendientes(data.pendientes);
      setFCAgente(data.fc_seleccion || "");
      setFiltroActivo({ etapaId: 0, subEtapaId: null });
    }
  }, [data]);

  // Funciones de Acción
  const verMas = (p: any) => {
    navigate(`/view/siniestro/${p.t_fc_siniestro}/${p.t_id}/proceso`);
  };

  const actualizarBandeja = () => {
    getPendientesAction();
    toastr.info("Actualizando bandeja...");
  };

  const getFiltro = (p: any, id: number | null, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    setFiltroActivo({ etapaId: p.id, subEtapaId: id });

    if (p.id === 0) {
      setPendientes(temp);
      return;
    }

    let np = [];
    if (id) {
      np = temp.filter((t: any) => t.t_fi_etapa === p.id && t.t_fi_sub_etapa === id);
    } else {
      np = temp.filter((t: any) => t.t_fi_etapa === p.id);
    }
    setPendientes(np);
  };

  const cambiarSeleccion = async (agenteId: string) => {
    setFCAgente(agenteId);
    try {
      const resp = await rmEspecialistaAction({
        fi_usuario_id: agenteId,
        fi_seleccion: 0,
      }).unwrap();

      if (resp.message) {
        getPendientesAction();
        toastr.success("Bandeja filtrada por ejecutivo");
      }
    } catch (err: any) {
      toastr.error(err?.data?.message || "Error al cambiar de bandeja");
    }
  };

  // Helpers visuales
  const totalTramites = temp.length;
  const isCargando = isLoading || isLoadingL || isLoadingE;
  const etapaActivaObj = data?.grupos?.find((g: any) => g.id === filtroActivo.etapaId);

  // Generador de iniciales para los avatares
  const getInitials = (nombre: string = "", apellido: string = "") => {
    return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase() || "US";
  };

  return (
    <div className="space-y-6 pb-10">

      {/* --- Intro & Fast Actions --- */}
      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight text-slate-900">
              Mis Trámites Pendientes
              <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-brand-700">En Vivo</span>
            </h1>
            <p className="mt-0.5 text-xs text-slate-500">
              Control y distribución operativa de folios de siniestros por analista y fase del proceso.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={actualizarBandeja}
              disabled={isCargando}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
            >
              <svg className={`h-3.5 w-3.5 text-slate-500 ${isCargando ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
              <span>Actualizar</span>
            </button>
          </div>
        </div>

        {/* --- Team Filters (Solo Supervisores) --- */}
        {!isLoading && usuario && roles.includes("siniestros-supervisor") && data?.especialistas?.length > 0 && (
          <div className="rounded-xl border border-slate-200/90 bg-white p-2 shadow-sm">
            <div className="mb-2 flex items-center justify-between border-b border-slate-100 px-2 pb-2 text-xs text-slate-500">
              <span className="flex items-center gap-1.5 font-medium text-slate-700">
                <svg className="h-3.5 w-3.5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
                Filtrar por Responsable Asignado:
              </span>
              <span className="text-[11px]">{data.especialistas.length} agentes activos hoy</span>
            </div>

            <div className="custom-scrollbar flex items-center gap-2 overflow-x-auto pb-1 text-xs">
              <button
                onClick={() => cambiarSeleccion("")}
                className={`group inline-flex shrink-0 items-center gap-2 rounded-lg px-3.5 py-2 font-medium transition ${fc_agente === ""
                  ? "bg-brand-600 text-white shadow-sm hover:bg-brand-700"
                  : "border border-slate-200 bg-slate-50/70 text-slate-700 hover:bg-slate-100"
                  }`}
              >
                <span>Todos los Responsables</span>
                <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-bold ${fc_agente === "" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"}`}>
                  {totalTramites}
                </span>
              </button>

              {data.especialistas.map((e: any) => (
                <button
                  key={e.id}
                  onClick={() => cambiarSeleccion(e.sub)}
                  className={`group inline-flex shrink-0 items-center gap-2 rounded-lg border px-3 py-1.5 font-medium transition ${fc_agente === e.sub
                    ? "border-brand-200 bg-brand-50/50 text-brand-900 ring-1 ring-brand-300"
                    : "border-slate-200 bg-slate-50/70 text-slate-700 hover:bg-slate-100"
                    }`}
                >
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ${fc_agente === e.sub ? "bg-brand-600 text-white" : "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200"}`}>
                    {getInitials(e.fc_nombre, e.fc_apellido_paterno)}
                  </span>
                  <span className="group-hover:text-slate-900">
                    {e.fc_apellido_paterno} {e.fc_nombre}
                  </span>
                  <span className={`rounded-md border px-2 py-0.5 text-xs font-bold ${fc_agente === e.sub ? "bg-brand-600 text-white shadow-xs" : "border-indigo-200/60 bg-indigo-50 text-indigo-700 group-hover:bg-indigo-100"}`}>
                    {e.cantidad}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {isCargando && !data ? (
        <div className="flex w-full justify-center py-10"><Spinner /></div>
      ) : (
        <>
          {data && data.pendientes.length === 0 ? (
            <section className="flex flex-col items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 py-16 text-center shadow-sm">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
              </div>
              <h2 className="text-xl font-bold text-emerald-900">¡Bandeja al día!</h2>
              <p className="mt-2 text-sm text-emerald-700">No tienes trámites pendientes asignados en este momento.</p>
            </section>
          ) : (
            <>
              {/* --- Pipeline KPI Cards --- */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xs font-bold uppercase tracking-wider text-slate-600">Embudo de Estados Operativos</h2>
                    <span className="text-[11px] text-slate-400">• Selecciona una etapa para filtrar</span>
                  </div>
                  <span className="text-xs font-medium text-slate-500">
                    Total en Proceso: <strong className="font-bold text-slate-900">{totalTramites} trámites</strong>
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
                  {/* Tarjeta de TODOS */}

                  {/* Tarjetas Dinámicas por Etapa */}
                  {data?.grupos?.map((g: any) => {
                    const isEtapaActiva = filtroActivo.etapaId === g.id;
                    const pct = totalTramites > 0 ? Math.round((g.cantidad / totalTramites) * 100) : 0;

                    return (
                      <div
                        key={g.id}
                        onClick={(e) => getFiltro(g, null, e)}
                        className={`group relative flex cursor-pointer flex-col justify-between overflow-hidden rounded-xl border p-3.5 transition-all ${isEtapaActiva
                          ? "border-brand-500 bg-gradient-to-b from-brand-600 to-brand-700 text-white shadow-md shadow-brand-500/20 ring-2 ring-brand-500 ring-offset-2"
                          : "border-slate-200 bg-white hover:border-brand-400"
                          }`}
                      >
                        {isEtapaActiva && <div className="pointer-events-none absolute -right-3 -top-3 h-16 w-16 rounded-full bg-white/10 blur-sm"></div>}

                        <div className="relative z-10 flex items-center justify-between">
                          <span className={`text-[11px] font-semibold uppercase tracking-tight ${isEtapaActiva ? "text-blue-100" : "text-slate-600 group-hover:text-brand-600"}`}>
                            {g.etapa.substring(0, 15)}{g.etapa.length > 15 ? "..." : ""}
                          </span>
                          <div className={`flex h-6 w-6 items-center justify-center rounded-md ${isEtapaActiva ? "bg-white/20 text-white shadow-xs" : "bg-slate-100 text-slate-400"}`}>
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                          </div>
                        </div>
                        <div className="relative z-10 mt-3">
                          <div className="flex items-baseline gap-2">
                            <span className={`text-2xl font-black tracking-tight ${isEtapaActiva ? "text-white" : "text-slate-800"}`}>{g.cantidad}</span>
                            {isEtapaActiva && <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-semibold text-white">{pct}%</span>}
                          </div>
                          <span className={`mt-0.5 block text-[10px] ${isEtapaActiva ? "font-medium text-blue-100" : "text-slate-500"}`}>
                            {isEtapaActiva ? "Etapa Seleccionada" : `${pct}% del volumen`}
                          </span>
                        </div>
                        <div className={`mt-2.5 h-1 w-full rounded-full ${isEtapaActiva ? "bg-white/20" : "bg-slate-100"}`}>
                          <div className={`h-1 rounded-full ${isEtapaActiva ? "bg-white" : "bg-brand-500"}`} style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Sub-breakdown Drawer (Aparece solo si la etapa tiene sub-etapas) */}
                {etapaActivaObj && etapaActivaObj.sub_etapa?.length > 0 && (
                  <div className="rounded-xl border border-blue-200/80 bg-gradient-to-r from-blue-50/90 via-sky-50/50 to-white p-4 shadow-sm animate-in fade-in slide-in-from-top-4 duration-300">
                    <div className="flex flex-col gap-2 border-b border-blue-100 pb-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-2">
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600"></span>
                        </span>
                        <h3 className="text-xs font-bold uppercase tracking-wide text-brand-900">
                          Desglose: Etapa "{etapaActivaObj.etapa}"
                        </h3>
                      </div>
                      <span className="rounded bg-blue-100/80 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                        {etapaActivaObj.sub_etapa.length} sub-estados registrados
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
                      {etapaActivaObj.sub_etapa.map((s: any) => {
                        const isSubActiva = filtroActivo.subEtapaId === s.id;
                        return (
                          <div
                            key={s.id}
                            onClick={(e) => getFiltro(etapaActivaObj, s.id, e)}
                            className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 shadow-xs transition hover:border-brand-300 ${isSubActiva ? "border-brand-400 bg-brand-50" : "border-blue-100 bg-white"
                              }`}
                          >
                            <div>
                              <span className={`block text-xs font-medium ${isSubActiva ? "text-brand-800 font-bold" : "text-slate-600"}`}>
                                {s.sub_etapa}
                              </span>
                            </div>
                            <div className="text-right">
                              <span className={`text-lg font-extrabold ${isSubActiva ? "text-brand-700" : "text-brand-500"}`}>{s.cantidad}</span>
                              <span className="block -mt-1 text-[10px] text-slate-400">trámites</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>

              {/* --- Table Section --- */}
              <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-3 border-b border-slate-100 bg-slate-50/50 p-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-sm font-bold text-slate-800">Detalle de Trámites Activos</h3>
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-brand-700">
                      Mostrando {pendientes.length} registros
                    </span>
                  </div>
                  {filtroActivo.etapaId !== 0 && (
                    <button
                      onClick={() => getFiltro({ id: 0 }, null)}
                      className="text-xs font-semibold text-brand-600 hover:text-brand-700 hover:underline"
                    >
                      Limpiar Filtro
                    </button>
                  )}
                </div>

                {/* Reutilizamos el componente de tabla (que ya adaptamos previamente al HTML) */}
                <div className="p-2 sm:p-4">
                  <TablaTramitesComponent
                    data={pendientes}
                    texto_accion="Gestionar"
                    accion={verMas}
                    ejecutivos={[]}
                    cargando={false}
                  />
                </div>
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
};
